import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const PrivacySettingsTab = ({ privacySettings, onUpdate }) => {
  const [settings, setSettings] = useState(privacySettings);
  const [hasChanges, setHasChanges] = useState(false);

  const languageOptions = [
    { value: 'fr', label: 'Français' },
    { value: 'ar', label: 'العربية' },
    { value: 'en', label: 'English' }
  ];

  const notificationFrequencyOptions = [
    { value: 'immediate', label: 'Immédiat' },
    { value: 'daily', label: 'Quotidien' },
    { value: 'weekly', label: 'Hebdomadaire' },
    { value: 'monthly', label: 'Mensuel' },
    { value: 'never', label: 'Jamais' }
  ];

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(settings);
    setHasChanges(false);
  };

  const handleReset = () => {
    setSettings(privacySettings);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      {/* Privacy Controls */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Shield" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Confidentialité des Données</h3>
        </div>

        <div className="space-y-6">
          <div className="space-y-4">
            <Checkbox
              label="Partager mon profil avec les établissements"
              description="Permet aux établissements de vous contacter directement avec des opportunités pertinentes"
              checked={settings?.shareProfileWithInstitutions}
              onChange={(e) => handleSettingChange('shareProfileWithInstitutions', e?.target?.checked)}
            />

            <Checkbox
              label="Utiliser mes données pour améliorer les recommandations"
              description="Aide à personnaliser les suggestions d'établissements et de programmes"
              checked={settings?.useDataForRecommendations}
              onChange={(e) => handleSettingChange('useDataForRecommendations', e?.target?.checked)}
            />

            <Checkbox
              label="Partager des statistiques anonymisées"
              description="Contribue aux recherches sur l'orientation étudiante au Maroc (données anonymes uniquement)"
              checked={settings?.shareAnonymousStatistics}
              onChange={(e) => handleSettingChange('shareAnonymousStatistics', e?.target?.checked)}
            />

            <Checkbox
              label="Afficher mon profil dans les recherches"
              description="Permet aux conseillers d'orientation de vous trouver et vous contacter"
              checked={settings?.showInSearch}
              onChange={(e) => handleSettingChange('showInSearch', e?.target?.checked)}
            />
          </div>

          <div className="pt-4 border-t border-border">
            <h4 className="font-medium text-foreground mb-4">Données Partagées</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Checkbox
                label="Informations académiques"
                description="Notes, filière, établissement"
                checked={settings?.shareAcademicInfo}
                onChange={(e) => handleSettingChange('shareAcademicInfo', e?.target?.checked)}
              />
              <Checkbox
                label="Préférences géographiques"
                description="Régions et villes préférées"
                checked={settings?.shareLocationPreferences}
                onChange={(e) => handleSettingChange('shareLocationPreferences', e?.target?.checked)}
              />
              <Checkbox
                label="Intérêts professionnels"
                description="Domaines d'intérêt et objectifs de carrière"
                checked={settings?.shareCareerInterests}
                onChange={(e) => handleSettingChange('shareCareerInterests', e?.target?.checked)}
              />
              <Checkbox
                label="Historique de navigation"
                description="Pages visitées et établissements consultés"
                checked={settings?.shareBrowsingHistory}
                onChange={(e) => handleSettingChange('shareBrowsingHistory', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Notification Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Bell" size={20} className="text-secondary" />
          <h3 className="text-lg font-semibold text-foreground">Préférences de Notification</h3>
        </div>

        <div className="space-y-6">
          <Select
            label="Fréquence des notifications"
            description="À quelle fréquence souhaitez-vous recevoir des notifications ?"
            options={notificationFrequencyOptions}
            value={settings?.notificationFrequency}
            onChange={(value) => handleSettingChange('notificationFrequency', value)}
          />

          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Types de Notifications</h4>
            
            <Checkbox
              label="Nouvelles recommandations"
              description="Recevoir des alertes pour de nouveaux établissements recommandés"
              checked={settings?.notifyNewRecommendations}
              onChange={(e) => handleSettingChange('notifyNewRecommendations', e?.target?.checked)}
            />

            <Checkbox
              label="Échéances d'admission"
              description="Rappels pour les dates limites de candidature"
              checked={settings?.notifyDeadlines}
              onChange={(e) => handleSettingChange('notifyDeadlines', e?.target?.checked)}
            />

            <Checkbox
              label="Mises à jour des établissements"
              description="Changements dans les programmes ou critères d'admission"
              checked={settings?.notifyInstitutionUpdates}
              onChange={(e) => handleSettingChange('notifyInstitutionUpdates', e?.target?.checked)}
            />

            <Checkbox
              label="Conseils d'orientation"
              description="Articles et conseils personnalisés pour votre parcours"
              checked={settings?.notifyOrientationTips}
              onChange={(e) => handleSettingChange('notifyOrientationTips', e?.target?.checked)}
            />

            <Checkbox
              label="Événements et salons"
              description="Informations sur les salons d'orientation et événements étudiants"
              checked={settings?.notifyEvents}
              onChange={(e) => handleSettingChange('notifyEvents', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Account Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Settings" size={20} className="text-accent" />
          <h3 className="text-lg font-semibold text-foreground">Paramètres du Compte</h3>
        </div>

        <div className="space-y-6">
          <Select
            label="Langue de l'interface"
            description="Choisissez la langue d'affichage de l'application"
            options={languageOptions}
            value={settings?.language}
            onChange={(value) => handleSettingChange('language', value)}
          />

          <div className="space-y-4">
            <Checkbox
              label="Activer l'authentification à deux facteurs"
              description="Sécurisez votre compte avec une vérification supplémentaire"
              checked={settings?.twoFactorAuth}
              onChange={(e) => handleSettingChange('twoFactorAuth', e?.target?.checked)}
            />

            <Checkbox
              label="Se souvenir de ma session"
              description="Rester connecté sur cet appareil"
              checked={settings?.rememberSession}
              onChange={(e) => handleSettingChange('rememberSession', e?.target?.checked)}
            />
          </div>
        </div>
      </div>
      {/* Data Management */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Icon name="Database" size={20} className="text-warning" />
          <h3 className="text-lg font-semibold text-foreground">Gestion des Données</h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <h4 className="font-medium text-foreground">Exporter mes données</h4>
              <p className="text-sm text-muted-foreground">
                Téléchargez une copie de toutes vos données personnelles
              </p>
            </div>
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
              Exporter
            </Button>
          </div>

          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <h4 className="font-medium text-foreground">Supprimer mon compte</h4>
              <p className="text-sm text-muted-foreground">
                Suppression définitive de votre compte et de toutes vos données
              </p>
            </div>
            <Button variant="destructive" size="sm" iconName="Trash2" iconPosition="left">
              Supprimer
            </Button>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      {hasChanges && (
        <div className="flex justify-end space-x-3 p-4 bg-muted/30 rounded-lg">
          <Button variant="outline" onClick={handleReset}>
            Annuler
          </Button>
          <Button variant="default" onClick={handleSave} iconName="Save" iconPosition="left">
            Enregistrer les Modifications
          </Button>
        </div>
      )}
      {/* Privacy Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <h4 className="font-medium text-blue-900 mb-1">Protection de vos données</h4>
            <p className="text-blue-800">
              Nous respectons votre vie privée et suivons les meilleures pratiques de sécurité. 
              Vos données sont chiffrées et ne sont jamais vendues à des tiers. 
              Consultez notre <a href="#" className="underline font-medium">politique de confidentialité</a> pour plus d'informations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacySettingsTab;